import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { Grid, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import DateService from "../../../services/dateService";

import BasicDialog from "../../base/BasicDialog";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import TextFieldFormControl from "../../field/TextFieldFormControl";

import { useForm } from "../../../hooks/useForm";

import AutocompleteField from "../../field/AutocompleteField";
import FloatField from "../../base/FloatField";

import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useSnackbarAlert } from "../../../context/snackbarAlert";

import useEquipmentService from "../../../services/equipmentService";

import {
  getErrorMsg,
  isEmptyValue,
  convertFlatListFromBackendToTranslatedOptionObject,
} from "../../../helpers/methods";

import {
  DIALOG_PREVIEW_MODE,
  DIALOG_EDIT_MODE,
  DIALOG_CREATE_MODE,
  EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE,
  EQUIPMENT_USAGE_METHOD_LIST,
  EQUIPMENT_USAGE_METHOD_READ_ONLY_LIST,
  EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION,
  EQUIPMENT_USAGE_METHOD_MANUAL,
} from "../../../helpers/constants";

const REQUIRED_FIELDS = [
  "equipment_bms_start",
  "equipment_bms_end",
  "eits_usage_value",
  "eits_usage_method",
];

const FIELDS_TO_SAVE = [...REQUIRED_FIELDS];

const EquipmentInstalationTenantSetUsageDialog = (props) => {
  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();

  const isReadOnly = useMemo(
    () => props.readOnly || props.dialogMode === DIALOG_PREVIEW_MODE,
    [props.readOnly, props.dialogMode]
  );

  const fieldsToSave = useMemo(
    () => props.fieldsToSave || FIELDS_TO_SAVE,
    [props.fieldsToSave]
  );

  const {
    formValue,
    setFormValue,
    onChange,
    onChangeAutocompleteFieldWithObjectOptions,
  } = useForm();

  const {
    getEquipmentInstalationTenantSetUsageDetails,
    updateEquipmentInstalationTenantSetUsage,
    getEquipmentBmsSelectList,
  } = useEquipmentService();

  const initialData = useAsync(() => {
    return getEquipmentInstalationTenantSetUsageDetails(props.eitsUsageId);
  }, [props.eitsUsageId, props.dialogMode]);

  useEffect(() => {
    setFormValue(initialData.value);
  }, [initialData.loading]);

  const prepareDataToSend = () => {
    const dataToSend = {};
    for (const [key, value] of Object.entries(formValue)) {
      if (fieldsToSave.includes(key)) {
        dataToSend[key] = typeof value === "object" ? value.id : value;
      }
    }

    return dataToSend;
  };

  const bmsSelectList = useAsync(() => {
    if (!formValue || !formValue?.equipment) return Promise.resolve(null);
    return getEquipmentBmsSelectList({ equipment: formValue?.equipment });
  }, [formValue?.equipment]);

  const updateEitsUsageFn = useAsyncFn(
    updateEquipmentInstalationTenantSetUsage
  );

  const handleSubmit = (dataToSend) => {
    updateEitsUsageFn
      .execute(dataToSend, props.eitsUsageId)
      .then((res) => {
        snackbarAlert.openSuccessSnackbarAlert(
          t("snackbar_alert.eits_usage_updated")
        );

        if (props.onSubmitCallback) {
          props.onSubmitCallback();
        }
        props.onClose();
      })
      .catch((error) => {
        snackbarAlert.openErrorSnackbarAlert(
          getErrorMsg(
            error.data,
            t("snackbar_alert.occurred_error_during_saving_changes")
          )
        );
      });
  };

  const [
    averageConsumptionCalculationDesription,
    setAverageConsumptionCalculationDesription,
  ] = useState();

  const isMethodAverageCompsumption =
    formValue?.eits_usage_method === EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION;

  const isMethodReadingDifference =
    formValue?.eits_usage_method === EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE;

  const calculateUsageUsingAverageCompsumptionMethod = (bmsStart, bmsEnd) => {
    const bmsStartDate = new Date(bmsStart?.equipbms_date).setHours(0, 0, 0, 0);
    const bmsEndDate = new Date(bmsEnd?.equipbms_date).setHours(0, 0, 0, 0);

    const dateDiff =
      DateService.getDaysBetweenDates(bmsStartDate, bmsEndDate) + 1;

    const valueDiff = bmsEnd?.equipbms_value - bmsStart?.equipbms_value;

    const { zeroHour: startMonth, endHour: endMonth } =
      DateService.getISOStringZeroAndEndHoursOfMonthAndYear(
        new Date(formValue.eits_usage_year, formValue.eits_usage_month, 0)
      );

    const startSettlement = DateService.getBiggerDate(
      formValue.eits_start_settlement
        ? new Date(formValue.eits_start_settlement)
        : new Date(startMonth),
      new Date(startMonth)
    );

    const endSettlement = DateService.getSmallerDate(
      formValue.eits_end_settlement
        ? new Date(formValue.eits_end_settlement)
        : new Date(endMonth),
      new Date(endMonth)
    );

    let dayInSettlement = DateService.getDaysBetweenDates(
      startSettlement,
      endSettlement
    );

    if (
      formValue.eits_start_settlement > startMonth &&
      formValue.eits_start_settlement < endMonth
    ) {
      dayInSettlement += 1;
    }

    setAverageConsumptionCalculationDesription(
      `Wyliczenie średniego zużycia na dzień na podstawie wybranych odczytów:
 ${valueDiff.toFixed(2)} / ${dateDiff} * ${dayInSettlement}`
    );

    return ((valueDiff / dateDiff) * dayInSettlement).toFixed(2);
  };

  const calculateUsageUsingReadingDifferenceMethod = (bmsStart, bmsEnd) => {
    return (bmsEnd?.equipbms_value - bmsStart?.equipbms_value).toFixed(2);
  };

  const handleChangeEquipmentBms = (...props) => {
    const { name, value } = onChangeAutocompleteFieldWithObjectOptions(
      ...props
    );

    const bmsStart = bmsSelectList.value.find(
      (bms) =>
        bms.id ===
        (name === "equipment_bms_start" ? value : formValue.equipment_bms_start)
    );

    const bmsEnd = bmsSelectList.value.find(
      (bms) =>
        bms.id ===
        (name === "equipment_bms_end" ? value : formValue.equipment_bms_end)
    );

    let eitsUsageMethod = EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE;
    let eitsUsageValue = formValue.eits_usage_value;
    if (isMethodAverageCompsumption) {
      eitsUsageValue = calculateUsageUsingAverageCompsumptionMethod(
        bmsStart,
        bmsEnd
      );
      eitsUsageMethod = EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION;
    }

    if (isMethodReadingDifference) {
      eitsUsageValue = calculateUsageUsingReadingDifferenceMethod(
        bmsStart,
        bmsEnd
      );
    }

    setFormValue((prev) => ({
      ...prev,
      eits_usage_value: eitsUsageValue,
      eits_usage_method: eitsUsageMethod,
      [name]: value,
    }));
  };

  const handleChangeEitsUsageValue = (e) => {
    setFormValue((prev) => ({
      ...prev,
      eits_usage_value: e.target.value,
      eits_usage_method: EQUIPMENT_USAGE_METHOD_MANUAL,
    }));
  };

  const handleChangeEitsUsageMethod = (...props) => {
    const { name, value } = onChangeAutocompleteFieldWithObjectOptions(
      ...props
    );

    const bmsStart = bmsSelectList.value.find(
      (bms) => bms.id === formValue.equipment_bms_start
    );

    const bmsEnd = bmsSelectList.value.find(
      (bms) => bms.id === formValue.equipment_bms_end
    );

    let eitsUsageValue = formValue.eits_usage_value;
    if (value === EQUIPMENT_USAGE_METHOD_AVERAGE_CONSUMPTION) {
      eitsUsageValue = calculateUsageUsingAverageCompsumptionMethod(
        bmsStart,
        bmsEnd
      );
    }

    if (value === EQUIPMENT_USAGE_METHOD_READING_DIFFERENCE) {
      eitsUsageValue = calculateUsageUsingReadingDifferenceMethod(
        bmsStart,
        bmsEnd
      );
    }

    setFormValue((prev) => ({
      ...prev,
      eits_usage_value: eitsUsageValue,
      [name]: value,
    }));
  };

  const isFormValid =
    formValue &&
    REQUIRED_FIELDS.every((field) => !isEmptyValue(formValue[field]));

  const isLoading =
    formValue === undefined || bmsSelectList.loading || !bmsSelectList.value;

  const getDialogContent = () => {
    if (isLoading) return <LoaderWrapper showLoader={true} />;

    return (
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        rowGap={1}
        columnSpacing={2}
      >
        <Grid item xs={12} sm={6}>
          <AutocompleteField
            name="equipment_bms_start"
            isObjectOption={true}
            value={formValue.equipment_bms_start}
            options={bmsSelectList.value}
            optionLabelKey={"equipbms_date_value"}
            label={t("dialog.eits_usage.equipment_bms_start")}
            onChange={handleChangeEquipmentBms}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  backgroundColor: option.bms_is_ed_accepted
                    ? "var(--matcha-green)"
                    : option.bms_is_equipment_usage_verified
                    ? "var(--blue-gray)"
                    : null,
                }}
              >
                {option["equipbms_date_value"]}
              </li>
            )}
            multiple={false}
            disableClearable
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AutocompleteField
            name="equipment_bms_end"
            isObjectOption={true}
            value={formValue.equipment_bms_end}
            options={bmsSelectList.value}
            optionLabelKey={"equipbms_date_value"}
            label={t("dialog.eits_usage.equipment_bms_end")}
            onChange={handleChangeEquipmentBms}
            renderOption={(props, option) => (
              <li
                {...props}
                style={{
                  backgroundColor: option.bms_is_ed_accepted
                    ? "var(--matcha-green)"
                    : option.bms_is_equipment_usage_verified
                    ? "var(--blue-gray)"
                    : null,
                }}
              >
                {option["equipbms_date_value"]}
              </li>
            )}
            multiple={false}
            disableClearable
          />
        </Grid>

        {isMethodAverageCompsumption &&
          averageConsumptionCalculationDesription && (
            <Grid item xs={12}>
              <Typography
                variant="h7"
                gutterBottom
                component="div"
                style={{
                  margin: 0,
                  color: "var(--primary)",
                  textTransform: "uppercase",
                }}
              >
                {averageConsumptionCalculationDesription}
              </Typography>
            </Grid>
          )}
        <Grid item xs={12} sm={6}>
          <AutocompleteField
            name="eits_usage_method"
            value={formValue.eits_usage_method}
            label={t("dialog.eits_usage.eits_usage_method")}
            options={convertFlatListFromBackendToTranslatedOptionObject(
              EQUIPMENT_USAGE_METHOD_LIST
            )}
            onChange={handleChangeEitsUsageMethod}
            isObjectOption={true}
            disableClearable
            getOptionDisabled={(option) =>
              EQUIPMENT_USAGE_METHOD_READ_ONLY_LIST.includes(option.id)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FloatField
            name={"eits_value"}
            label={t("dialog.eits_usage.eits_usage_value")}
            value={formValue.eits_usage_value}
            onChange={handleChangeEitsUsageValue}
            suffix={formValue.eits_usage_unit}
          />
        </Grid>
        {!isReadOnly && (
          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              color="primary"
              fullWidth
              loading={updateEitsUsageFn.loading}
              disabled={!isFormValid}
              onClick={() => handleSubmit(prepareDataToSend())}
            >
              {t("save")}
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      contentAlign="center"
      showTopFullScreenButton={false}
      title={t("dialog.eits_usage.calculate_usage")}
      maxWidth="md"
      showDialogActions
    >
      {getDialogContent()}
    </BasicDialog>
  );
};

EquipmentInstalationTenantSetUsageDialog.propTypes = {
  dialogMode: PropTypes.oneOf([
    DIALOG_CREATE_MODE,
    DIALOG_EDIT_MODE,
    DIALOG_PREVIEW_MODE,
  ]),
  readOnly: PropTypes.bool,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmitCallback: PropTypes.func,
  fieldsToSave: PropTypes.array,
};

EquipmentInstalationTenantSetUsageDialog.defaultProps = {
  open: false,
};

export default EquipmentInstalationTenantSetUsageDialog;
